<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import '@sapudex/web-components/dist/features/SearchSuggestionsPopover';
import '@sapudex/web-components/dist/Search.js';

const PARTNER_NAVIGATOR_PAGE = 'partnerNavigatorPage';
const PARTNER_PROFILE_PAGE = 'profilePage';

const suggestionJustSelected = ref(false);
const isLoading = ref(false);
const formattedSuggestionList = ref('');
const timer = ref(null);
const searchInputRef = ref(null);
const localQuery = ref('');

const store = useStore();
const { t } = useI18n();
const router = useRouter();

formattedSuggestionList.value = getEmptySuggestionList();

const props = defineProps({
  isHomePage: {
    type: Boolean,
    default: false,
  },
});


const query = computed({
  get: () => store.state.profileSearch?.query || '',
  set: (value) => {
    if (props.isHomePage) {
      store.dispatch('profileSearch/setQueryWithoutSearch', value);
    } else {
      if (value === '') {
        store.commit('profileSearch/setQuery', null);
      } else {
        store.commit('profileSearch/setQuery', value);
      }
    }
  },
});

const searchResults = computed(() => store.state.autocomplete?.searchResults || []);
const popoverStateText = computed(() =>
  isLoading.value ? t('search.block.loading') : t('search.block.no.results')
);

const searchMode = computed(() => {
  return props.isHomePage ? 'Standard' : 'Home Search';
});
const searchModeClasses = computed(() => {
  return props.isHomePage ? 'home-search-input' : 'result-page-search-input';
});

const shouldShowSuggestions = computed(() => {
  return localQuery.value && searchResults.value.length > 0 ? true : null;
});

function updateSuggestionList(results) {
  if (!results || results.length === 0) {
    formattedSuggestionList.value = getEmptySuggestionList();
    return;
  }
  const suggested = results
    .filter((item) => item.title !== 'title')
    .map((item) => ({
      label: item.value,
      icon: 'sap-icon://search',
    }));

  if (suggested.length > 0) {
    formattedSuggestionList.value = JSON.stringify({
      suggested: {
        label: t('search.suggestions'),
        list: suggested,
      },
      recommended: {
        label: t('search.recommended'),
        list: [],
      },
    });
  } else {
    formattedSuggestionList.value = getEmptySuggestionList();
  }
}

function getEmptySuggestionList() {
  return JSON.stringify({
    suggested: {
      label: t('search.suggestions'),
      list: [],
    },
    recommended: {
      label: t('search.recommended'),
      list: [],
    },
  });
}

function isPartnerNavigationPage() {
  return (router.currentRoute.value.name === PARTNER_NAVIGATOR_PAGE);
}
function isProfilePage() {
  return (router.currentRoute.value.name === PARTNER_PROFILE_PAGE)
}
function handleHomePageSearch(searchValue) {
  if (window.$eventBus) {
    window.$eventBus.$emit('PFAACustomEvent', {
      eventName: 'SearchInitiated',
      custom2: searchValue,
      custom3: window.location.search,
      custom4: true,
    });
  }
  store.dispatch('profileSearch/homeSearchAddRouterQuery', {
    query: searchValue,
    guidedSearch: false,
  });
}

function setupResultsPageSearch(searchValue) {
  store.dispatch('autocomplete/abortController');
  store.commit('autocomplete/setShowSearchSuggestion', false);
  store.commit('profileSearch/cleanFilters');
  store.commit('profileSearch/cleanResults');
  store.commit('profileSearch/setPageNumber', 1);
  store.dispatch('solutionL2Store/clearSelectedSolution', {}, { root: true });

  return {
    q: searchValue,
    queryType: store.state.profileSearch?.queryTermTypeState || 'partner',
  };
}

function handleResultsPageSearch(searchValue) {
  const cleanQuery = setupResultsPageSearch(searchValue);

  store.dispatch('profileSearch/setQuery', searchValue);

  router
    .replace({
      query: cleanQuery,
    })
    .catch(() => {});

  if (window.$eventBus && !suggestionJustSelected.value) {
    window.$eventBus.$emit('PFAACustomEvent', {
      eventName: 'SearchInitiated',
      custom2: searchValue,
    });
  }
}

function handleHomePageSearchOnSugestionSelect(searchValue) {
  const cleanQuery = setupResultsPageSearch(searchValue);

  store.dispatch('profileSearch/setPathChangedFromUiFlag', false);
  router
    .push({
      name: 'partnerNavigatorPage',
      query: cleanQuery,
    })
    .catch(() => {});

  if (window.$eventBus && !suggestionJustSelected.value) {
    window.$eventBus.$emit('PFAACustomEvent', {
      eventName: 'SearchInitiated',
      custom2: searchValue,
      custom3: window.location.search,
      custom4: false,
    });
  }
}

function handleProfilePageSearch(searchValue) {
  store.dispatch('autocomplete/abortController');
  store.commit('autocomplete/setShowSearchSuggestion', false);

  const cleanQuery = {
    q: searchValue,
    queryType: store.state.profileSearch?.queryTermTypeState || 'partner',
  };

  store.dispatch('profileSearch/setPathChangedFromUiFlag', false);
  router
  .push({
    name: 'partnerNavigatorPage',
      query: cleanQuery,
    })
    .catch(() => {});

  if (window.$eventBus && !suggestionJustSelected.value) {
    window.$eventBus.$emit('PFAACustomEvent', {
      eventName: 'SearchInitiated',
      custom2: searchValue,
      custom3: window.location.search,
    });
  }
}

function handleSearch(event) {
  const searchValue = event.detail?.value || '';
  

  localQuery.value = searchValue;
  query.value = searchValue;
  store.commit('profileSearch/changeTriggeredQuery', searchValue);
  
  if (props.isHomePage) {
    if (suggestionJustSelected.value) {
      handleHomePageSearchOnSugestionSelect(searchValue);
    } else {
      handleHomePageSearch(searchValue);
    }
  } else if (isProfilePage()) {
    handleProfilePageSearch(searchValue);
  } else {
    handleResultsPageSearch(searchValue);
  }

  suggestionJustSelected.value = false;
}

function handleSuggestionSelect(event) {
  const selectedValue = event.detail.value || '';
  
  localQuery.value = selectedValue;
  query.value = selectedValue; 
  suggestionJustSelected.value = true;

  if (window.$eventBus) {
    window.$eventBus.$emit('PFAACustomEvent', {
      eventName: 'ClickSearchSuggestionItem',
      custom2: selectedValue,
    });
  }
}

function handleInput(event) {
  const value = event.detail?.value || (event.detail && event.detail.query) || '';
  localQuery.value = value;
  query.value = value;
}

function handleClear() {
  localQuery.value = '';
  query.value = '';
  store.commit('profileSearch/changeTriggeredQuery', '');
  store.dispatch('autocomplete/clearResults');
  store.commit('autocomplete/setShowSearchSuggestion', false);
  
  if (!isPartnerNavigationPage()) {
    store.dispatch('profileSearch/setQueryWithoutSearch', '');

    if (props.isHomePage) {
      store.dispatch('profileSearch/setPathChangedFromUiFlag', true);
      store.dispatch('profileSearch/homeSearchAddRouterQuery', {
        query: '',
        guidedSearch: true,
      });
      store.dispatch('profileSearch/findResults', 'results/count');
    }
  } else {
    store.dispatch('profileSearch/setQuery', '');
  }
}

function getPlaceholderText() {
  return props.isHomePage
    ? t('search.partner.placeholder.optional')
    : t('search.partner.placeholder');
}

watch(
  searchResults,
  (newResults) => {
    isLoading.value = false;
    updateSuggestionList(newResults);
  },
  { immediate: true, deep: true }
);

watch(
  query,
  (newValue, oldValue) => {
    store.commit('autocomplete/newValue', newValue);
    if (oldValue != null && props.isHomePage) {
      if (timer.value) {
        clearTimeout(timer.value);
      }

      timer.value = setTimeout(() => {
        store.commit('profileSearch/changeTriggeredQuery', newValue);
        store.dispatch('profileSearch/setPathChangedFromUiFlag', true);
        store.dispatch('profileSearch/homeSearchAddRouterQuery', {
          query: newValue,
          guidedSearch: true,
        });
      }, 300);
    }

    if (newValue) {
      isLoading.value = true;
      store.dispatch('autocomplete/getAutocompleteResults');
    } else {
      store.commit('autocomplete/setResults', []);
      store.commit('autocomplete/setShowSearchSuggestion', false);
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (store.state.profileSearch && store.state.profileSearch.query) {
    const existingQuery = store.state.profileSearch.query;
    localQuery.value = existingQuery;
    
    if (searchInputRef.value) {
      searchInputRef.value.value = existingQuery;
    }
    
    store.commit('autocomplete/newValue', existingQuery);
    store.dispatch('autocomplete/getAutocompleteResults');
  }
});

onBeforeUnmount(() => {
  if (timer.value) {
    clearTimeout(timer.value);
  }
});
</script>
<template>
  <udex-search
    ref="searchInputRef"
    class="w-full"
    :class="searchModeClasses"
    :label="getPlaceholderText()"
    language-voice="en-US"
    :mode="searchMode"
    :no-result-text="popoverStateText"
    show-submit-button
    :suggestion-list="formattedSuggestionList"
    show-suggested-popover
    :show-popover-when-clicked-into-input="shouldShowSuggestions"
    :label-animation-disabled="props.isHomePage"
    @handle-value-reset="handleClear"
    @handle-value-search="handleInput"
    @submit-search-value="handleSearch"
    @dropdown-item-click="handleSuggestionSelect"
  >
  </udex-search>
</template>

<style lang="less" scoped>
udex-search.home-search-input {
  --udex-input-background-color: white;
  --udex-input-radius: 0px;
  --udex-input-padding-vertical: 9px;
  max-width: 490px;
}
udex-search.result-page-search-input {
  --udex-input-background-color: white;
}
</style>